import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, MessageCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { localizeDeep } from "@/lib/localize";
import { Link } from "react-router-dom";

export default function Contact() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  useSEO("Contact INVIXA", "Get in touch with the INVIXA team.");
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [rawC, setRawC] = useState({});
  const c = localizeDeep(rawC, lang);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { api.get("/content/contact").then((r) => setRawC(r.data.value || {})).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent!");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16">
      <SectionHeading eyebrow={t("nav_contact")} title={c.heading || "Get in touch"} subtitle={c.intro || "Questions about VIP, brokers or education? Send us a message."} />
      <div className="mt-10 grid lg:grid-cols-2 gap-8">
        <form onSubmit={submit} className="card-surface rounded-2xl p-7 space-y-4" data-testid="contact-form">
          <div><Label className="text-slate-300">{t("name")}</Label><Input required value={form.name} onChange={set("name")} data-testid="contact-name" className="mt-1.5 bg-white/5 border-white/10" /></div>
          <div><Label className="text-slate-300">{t("email")}</Label><Input type="email" required value={form.email} onChange={set("email")} data-testid="contact-email" className="mt-1.5 bg-white/5 border-white/10" /></div>
          <div><Label className="text-slate-300">Subject</Label><Input value={form.subject} onChange={set("subject")} data-testid="contact-subject" className="mt-1.5 bg-white/5 border-white/10" /></div>
          <div><Label className="text-slate-300">Message</Label><Textarea required rows={5} value={form.message} onChange={set("message")} data-testid="contact-message" className="mt-1.5 bg-white/5 border-white/10" /></div>
          <Button type="submit" disabled={loading} data-testid="contact-submit" className="w-full gradient-brand text-brand-navy font-semibold"><Send className="h-4 w-4 mr-2" /> {loading ? t("loading") : t("submit")}</Button>
        </form>
        <div className="space-y-4">
          <div className="card-surface rounded-2xl p-7">
            <Mail className="h-6 w-6 text-brand-cyan mb-3" />
            <h3 className="font-display font-semibold text-white">Email</h3>
            {c.support_email ? <a href={`mailto:${c.support_email}`} className="text-sm text-brand-cyan mt-1 inline-block">{c.support_email}</a> : <p className="text-sm text-slate-400 mt-1">[PLACEHOLDER — support email to be provided]</p>}
          </div>
          {(c.whatsapp_url || c.telegram_url) && (
            <div className="card-surface rounded-2xl p-7 space-y-3">
              <h3 className="font-display font-semibold text-white">Chat with us</h3>
              {c.whatsapp_url && <a href={c.whatsapp_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-slate-300 hover:text-brand-green" data-testid="contact-whatsapp"><MessageCircle className="h-4 w-4 text-brand-green" /> {c.whatsapp_label || "WhatsApp"}</a>}
              {c.telegram_url && <a href={c.telegram_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-slate-300 hover:text-brand-cyan" data-testid="contact-telegram"><Send className="h-4 w-4 text-brand-cyan" /> {c.telegram_label || "Telegram"}</a>}
            </div>
          )}
          <div className="card-surface rounded-2xl p-7">
            <MessageCircle className="h-6 w-6 text-brand-turquoise mb-3" />
            <h3 className="font-display font-semibold text-white">Registered member?</h3>
            <p className="text-sm text-slate-400 mt-1">{c.business_note || "For account-specific help, use our Support Center to open a ticket."}</p>
            <Link to={user ? "/dashboard/tickets" : "/login"} className="text-sm text-brand-cyan mt-2 inline-block" data-testid="contact-support-link">Go to Support →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
